<template>
  <index-view-layout
    data-name="Contact"
    :side-drawer-component="sideDrawerComponent"
    @search="searchContacts"
    searchable
  >
    <div class="flex flex-col justify-between w-full" v-if="selectedItems">
      <stacked-tabs
        :tabs="tabs"
        :selected-tab="selectedTab"
        @tab-selected="selectedTab = $event"
      />

      <bdg-table-view
        :rows="selectedItems"
        :columns="columns"
        :page-options="{ isPaged: selectedItems.length > 10, itemsPerPage: 10 }"
        :empty-state-text="emptyStateText"
        :loading="isLoading"
      >
        <template #above-table>
          <div class="sub-tabs w-full px-5 py-6 md:px-6" v-if="subTabs">
            <stacked-tabs
              class="font-cond text-center uppercase"
              :tabs="subTabs"
              :selected-tab="selectedSubTab"
              @tab-selected="selectedSubTab = $event"
            />
          </div>
        </template>
      </bdg-table-view>
      <template v-if="$mq === 'sm'">
        <contact-cell
          v-for="(contact, index) in selectedItems"
          :key="`${contact.name}-${index}`"
          :contact="contact"
        />
      </template>
    </div>
  </index-view-layout>
</template>

<script>
import StackedTabs from "../elements/StackedTabs";
import IndexViewLayout from "./layouts/IndexViewLayout";
import BdgTableView from "../elements/table-view/BdgTableView";
import ContactCell from "../elements/list-view/list-view-cells/ContactCell";
import ContactForm from "../forms/ContactForm";
import {
  ContactTypes,
  ContactTypesCondensed,
  InternalSubtypes
} from "../constants/contact-enums";
import AddressCell from "../elements/table-view/special-cells/AddressCell";
import GenericDeleteModal from "../components/modals/GenericDeleteModal";
import EmailCell from "../elements/table-view/special-cells/EmailCell";
import CompanyAndContactNamesCell from "../elements/table-view/special-cells/CompanyAndContactNamesCell";
import { sortContacts } from "../helpers";

export default {
  name: "Contacts",
  components: {
    ContactCell,
    BdgTableView,
    IndexViewLayout,
    StackedTabs
  },
  data() {
    return {
      selectedTab: null,
      partnersAndVendorsSelectedSubTab: null,
      internalStaffSelectedSubTab: null,
      sideDrawerComponent: ContactForm,
      columns: [
        {
          label: "name",
          property: "companyAndContactNames",
          cellComponent: CompanyAndContactNamesCell
        },
        {
          label: "address",
          property: "nestedPlaces",
          cellComponent: AddressCell
        },
        { label: "email", property: "nestedEmail", cellComponent: EmailCell },
        { label: "phone", property: "primaryPhoneDisplayString" },
        {
          label: "actions",
          property: "actions",
          actions: [
            {
              label: "view",
              clickAction: contact => {
                this.$router.push({
                  name: "contact-show",
                  params: { id: contact.id }
                });
              }
            },
            {
              label: "edit",
              clickAction: contact => {
                this.$store.commit("setCreateEditComponentParams", {
                  createEditComponent: ContactForm,
                  createEditComponentData: contact,
                  createEditComponentIsShown: true
                });
              }
            },
            {
              label: "delete",
              clickAction: contact => {
                this.$store.commit("setConfirmationModalParams", {
                  type: "delete",
                  headerText: "Delete Confirmation",
                  bodyComponent: GenericDeleteModal,
                  bodyComponentData: {
                    entityName: "contact",
                    deleteFunction: () =>
                      this.$store.dispatch("deleteContact", contact)
                  },
                  isShown: true
                });
              }
            }
          ],
          additionalCellClasses: "w-px whitespace-no-wrap"
        }
      ]
    };
  },
  computed: {
    tabs() {
      return this.$store.state.Contacts.indexTabs.map(t => {
        if (
          t.name === "part. & vend." &&
          ["sm", "md"].indexOf(this.$mq) === -1
        ) {
          t.name = "Partners & Vendors";
        }
        return t;
      });
    },
    partnersAndVendorsSubTabs() {
      return this.$store.state.Contacts.partnersAndVendorsSubTabs;
    },
    internalStaffSubTabs() {
      return this.$store.state.Contacts.internalStaffSubTabs;
    },
    subTabs() {
      if (this.selectedTab) {
        switch (this.selectedTab.slug) {
          case ContactTypesCondensed.PARTNER_VENDOR:
            return this.partnersAndVendorsSubTabs;
          case ContactTypesCondensed.INTERNAL:
            return this.internalStaffSubTabs;
          default:
            return null;
        }
      }
      return null;
    },
    selectedSubTab: {
      get() {
        if (this.selectedTab) {
          switch (this.selectedTab.slug) {
            case ContactTypesCondensed.PARTNER_VENDOR:
              return this.partnersAndVendorsSelectedSubTab;
            case ContactTypesCondensed.INTERNAL:
              return this.internalStaffSelectedSubTab;
            default:
              return null;
          }
        }
        return null;
      },
      set(val) {
        if (this.selectedTab) {
          switch (this.selectedTab.slug) {
            case ContactTypesCondensed.PARTNER_VENDOR:
              this.partnersAndVendorsSelectedSubTab = val;
              break;
            case ContactTypesCondensed.INTERNAL:
              this.internalStaffSelectedSubTab = val;
              break;
          }
        }
      }
    },
    emptyStateText() {
      if (this.selectedTab) {
        switch (this.selectedTab.slug) {
          case "all-contacts":
            return "No contacts to view.";
          case "customers":
            return "No customers to view.";
          case "part-vend":
            return "No partners or vendors to view.";
          case "internal-staff":
            return "No internal staff members to view.";
          default:
            return "No data to view.";
        }
      }
      return null;
    },
    showSearchResults() {
      return this.$store.state.Search.areSearchResultsShown;
    },
    isGettingContacts() {
      return this.$store.state.Contacts.isGettingContacts;
    },
    isSearchingContacts() {
      return this.$store.state.Search.isSearchingContacts;
    },
    isLoading() {
      return this.showSearchResults
        ? this.isSearchingContacts
        : this.isGettingContacts;
    },
    contacts() {
      return this.$store.state.Contacts.contacts;
    },
    searchedContacts() {
      return this.$store.state.Search.searchedContacts;
    },
    displayedContacts() {
      let contacts = this.showSearchResults
        ? this.searchedContacts
        : this.contacts;
      if (contacts) {
        contacts = contacts.map(c => {
          return {
            ...c,
            primaryPhoneDisplayString: c.primaryPhoneDisplayString,
            nestedPlaces: { place: c.primary_place },
            nestedEmail: { email: c.email },
            companyAndContactNames: {
              companyName: c.company_name,
              name: c.name
            }
          };
        });

        contacts.sort(sortContacts);
      }
      return contacts;
    },
    selectedItems() {
      if (this.selectedTab && this.displayedContacts) {
        return this.displayedContacts.filter(contact => {
          if (this.selectedTab.slug !== "all") {
            if (
              this.selectedTab.slug === ContactTypesCondensed.PARTNER_VENDOR
            ) {
              if (
                !this.selectedSubTab ||
                (this.selectedSubTab && this.selectedSubTab.slug === "all")
              ) {
                return (
                  contact.type === ContactTypes.PARTNER ||
                  contact.type === ContactTypes.VENDOR
                );
              }
              return (
                (contact.type === ContactTypes.PARTNER ||
                  contact.type === ContactTypes.VENDOR) &&
                contact.subtype === this.selectedSubTab.slug
              );
            } else if (
              this.selectedTab.slug === ContactTypesCondensed.INTERNAL
            ) {
              if (
                !this.selectedSubTab ||
                (this.selectedSubTab && this.selectedSubTab.slug === "all")
              ) {
                return contact.type === ContactTypes.INTERNAL;
              } else if (
                this.selectedSubTab &&
                this.selectedSubTab.slug === "crew"
              ) {
                return (
                  contact.type === ContactTypes.INTERNAL &&
                  (contact.subtype === InternalSubtypes.CREW_SUPERVISOR ||
                    contact.subtype === InternalSubtypes.CREW_LABORER)
                );
              }
              return (
                contact.type === ContactTypes.INTERNAL &&
                contact.subtype === this.selectedSubTab.slug
              );
            }
            return contact.type === this.selectedTab.slug;
          }
          return true;
        });
      }
      return [];
    }
  },
  watch: {
    searchedContacts(newVal, oldVal) {
      if (
        newVal !== oldVal &&
        newVal &&
        newVal.length &&
        this.showSearchResults
      ) {
        this.selectedTab = this.tabs[0];
      }
    }
  },
  created() {
    this.$store.dispatch("getContacts");
  },
  methods: {
    searchContacts(text) {
      if (text) {
        this.$store.dispatch("searchContacts", text);
      } else {
        this.$store.commit("setAreSearchResultsShown", false);
      }
    }
  }
};
</script>
